import { useMemo } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { AccountId, PermissionId, ProjectId } from 'api/models';
import { EventType, ProjectChannel, pushes, SocketData } from 'api/pushes';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';

export const useMultiProjectWsUpdates = <T>(
  projectIds: ProjectId[],
  handler: (socketData: SocketData<T>) => void,
  [action, objectType]: EventType,
): void => {
  const userInfo = useUser();
  const { accountId } = useAccount();
  const pairs = useMemo(
    () =>
      projectIds
        .map(
          ([, projectId]) =>
            [
              projectId,
              userInfo.permissions.projects[projectId]?.permissionSet
                ?.permissionSetId,
            ] as [number, number | undefined],
        )
        .filter(([, permissionId]) => permissionId != null)
        .map(([k, v]) => [k, v!]),
    [projectIds, userInfo.permissions.projects],
  );

  useDeepCompareEffect(() => {
    const subscriptions = pairs.map(([projectId, permissionId]) =>
      pushes<T>(
        ProjectChannel(
          AccountId(accountId),
          PermissionId(permissionId),
          ProjectId(projectId),
        ),
        [action, objectType],
      ).subscribe(handler),
    );

    return () => {
      subscriptions.map((s) => s.unsubscribe());
    };
  }, [accountId, action, objectType, handler, pairs]);
};
